import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";

// Eagerly loaded, best for LCP
import { HomePage } from "./pages/Home";

// Lazy-loaded marketing pages
const PricingPage = lazy(() =>
  import("./pages/Pricing").then((m) => ({ default: m.PricingPage }))
);
const ProductsPage = lazy(() =>
  import("./pages/ProductsPage").then((m) => ({ default: m.ProductsPage }))
);
const ServicesPage = lazy(() =>
  import("./pages/ServicesPage").then((m) => ({ default: m.ServicesPage }))
);
const FaqPage = lazy(() =>
  import("./pages/Faq").then((m) => ({ default: m.FaqPage }))
);
const SupportPage = lazy(() =>
  import("./pages/Support").then((m) => ({ default: m.SupportPage }))
);
const AuthPage = lazy(() =>
  import("./pages/Auth").then((m) => ({ default: m.AuthPage }))
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPassword").then((m) => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPassword").then((m) => ({ default: m.ResetPasswordPage }))
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage }))
);

// Stage-one redesign preview (new "Buddy" design system) — lives at /preview/*,
// entirely separate from the live site above, until it's reviewed and cut over.
const PreviewLanding = lazy(() => import("./pages/preview/Landing"));
const PreviewPricing = lazy(() => import("./pages/preview/Pricing"));
const PreviewLogin = lazy(() => import("./pages/preview/Login"));
const PreviewOnboarding = lazy(() => import("./pages/preview/Onboarding"));
const PreviewCheckout = lazy(() => import("./pages/preview/Checkout"));
const PreviewDashboard = lazy(() => import("./pages/preview/Dashboard"));
const PreviewStatus = lazy(() => import("./pages/preview/Status"));
const PreviewDevDocs = lazy(() => import("./pages/preview/DevDocs"));
const PreviewPartner = lazy(() => import("./pages/preview/Partner"));
const PreviewLegal = lazy(() => import("./pages/preview/Legal"));
const PreviewSystemPages = lazy(() => import("./pages/preview/SystemPages"));
const PreviewEmails = lazy(() => import("./pages/preview/Emails"));
const PreviewForgotPassword = lazy(() => import("./pages/preview/ForgotPassword"));
const PreviewVerifyEmail = lazy(() => import("./pages/preview/VerifyEmail"));
const PreviewAcceptInvite = lazy(() => import("./pages/preview/AcceptInvite"));
const PreviewTwoFactorSetup = lazy(() => import("./pages/preview/TwoFactorSetup"));
const PreviewFirstRun = lazy(() => import("./pages/preview/FirstRun"));
const PreviewAgentDesk = lazy(() => import("./pages/preview/AgentDesk"));
const PreviewAdmin = lazy(() => import("./pages/preview/Admin"));

// Dashboard, layout and pages (all lazy)
const DashboardLayout = lazy(() =>
  import("./layouts/DashboardLayout").then((m) => ({
    default: m.DashboardLayout
  }))
);

const DashboardHome = lazy(() =>
  import("./pages/dashboard/DashboardHome").then((m) => ({
    default: m.DashboardHome
  }))
);

// These three pages use **default export** in their files
const VoiceRequestsPage = lazy(() => import("./pages/dashboard/VoiceRequestsPage"));
const BillingPage = lazy(() => import("./pages/dashboard/BillingPage"));
const ConversationsPage = lazy(
  () => import("./pages/dashboard/ConversationsPage")
);
const ProfilePage = lazy(() => import("./pages/dashboard/ProfilePage"));
const VerifyInvoicePage = lazy(() => import("./pages/VerifyInvoicePage"));
const DemosPage = lazy(() => import("./pages/Demos").then((m) => ({ default: m.DemosPage })));

const LazyLoadingFallback = () => (
  <div className="flex items-center justify-center py-16 text-sm text-gray-500">
    Loading...
  </div>
);

// Small helper to avoid repeating <Suspense> everywhere
function withSuspense(element: React.ReactElement) {
  return <Suspense fallback={<LazyLoadingFallback />}>{element}</Suspense>;
}

export default function App() {
  return (
    <Routes>
      {/* Home stays eager for best LCP */}
      <Route path="/" element={<HomePage />} />

      {/* Marketing pages */}
      <Route path="/pricing" element={withSuspense(<PricingPage />)} />
      <Route path="/products" element={withSuspense(<ProductsPage />)} />
      <Route path="/services" element={withSuspense(<ServicesPage />)} />
      <Route path="/faq" element={withSuspense(<FaqPage />)} />
      <Route path="/support" element={withSuspense(<SupportPage />)} />

      {/* Auth */}
      <Route
        path="/login"
        element={withSuspense(<AuthPage initialMode="login" />)}
      />
      <Route
        path="/signup"
        element={withSuspense(<AuthPage initialMode="signup" />)}
      />
      <Route
        path="/forgot-password"
        element={withSuspense(<ForgotPasswordPage />)}
      />
      <Route
        path="/reset-password"
        element={withSuspense(<ResetPasswordPage />)}
      />

      {/* Dashboard */}
      <Route path="/dashboard" element={withSuspense(<DashboardLayout />)}>
        <Route index element={withSuspense(<DashboardHome />)} />
        <Route path="orders" element={withSuspense(<VoiceRequestsPage />)} />
        <Route path="voice" element={withSuspense(<VoiceRequestsPage />)} />
        <Route path="billing" element={withSuspense(<BillingPage />)} />
        <Route
          path="conversations"
          element={withSuspense(<ConversationsPage />)}
        />
        <Route path="profile" element={withSuspense(<ProfilePage />)} />
      </Route>

      {/* Invoice Verification */}
      <Route
        path="/verify-invoice/:invoiceId"
        element={withSuspense(<VerifyInvoicePage />)}
      />

      {/* New Demos Page */}
      <Route path="/demos" element={withSuspense(<DemosPage />)} />

      {/* Stage-one redesign preview */}
      <Route path="/preview" element={withSuspense(<PreviewLanding />)} />
      <Route path="/preview/pricing" element={withSuspense(<PreviewPricing />)} />
      <Route path="/preview/login" element={withSuspense(<PreviewLogin />)} />
      <Route path="/preview/signup" element={withSuspense(<PreviewOnboarding />)} />
      <Route path="/preview/checkout" element={withSuspense(<PreviewCheckout />)} />
      <Route path="/preview/dashboard" element={withSuspense(<PreviewDashboard />)} />
      <Route path="/preview/status" element={withSuspense(<PreviewStatus />)} />
      <Route path="/preview/docs" element={withSuspense(<PreviewDevDocs />)} />
      <Route path="/preview/partner" element={withSuspense(<PreviewPartner />)} />
      <Route path="/preview/legal" element={withSuspense(<PreviewLegal />)} />
      <Route path="/preview/system" element={withSuspense(<PreviewSystemPages />)} />
      <Route path="/preview/emails" element={withSuspense(<PreviewEmails />)} />
      <Route path="/preview/forgot-password" element={withSuspense(<PreviewForgotPassword />)} />
      <Route path="/preview/verify-email" element={withSuspense(<PreviewVerifyEmail />)} />
      <Route path="/preview/accept-invite" element={withSuspense(<PreviewAcceptInvite />)} />
      <Route path="/preview/2fa-setup" element={withSuspense(<PreviewTwoFactorSetup />)} />
      <Route path="/preview/firstrun" element={withSuspense(<PreviewFirstRun />)} />
      <Route path="/preview/agentdesk" element={withSuspense(<PreviewAgentDesk />)} />
      <Route path="/preview/admin" element={withSuspense(<PreviewAdmin />)} />

      {/* 404 */}
      <Route path="*" element={withSuspense(<NotFoundPage />)} />
    </Routes>
  );
}
